// Screenplay Conversion Landing Page
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Layout } from '../components/Layout';
import { 
  Film, Tv, CheckCircle, Star, Sparkles, TrendingUp,
  Clock, FileText, Award, Zap, ArrowRight, BookOpen
} from 'lucide-react';

export function ScreenplayConversionLanding() {
  const navigate = useNavigate();
  const [selectedFormat, setSelectedFormat] = useState('screenplay');

  const formats = {
    screenplay: {
      name: 'Feature Screenplay',
      price: 39,
      icon: Film,
      pages: '90-120',
      time: '5-10',
      features: [
        '10-sequence professional structure',
        'Industry-standard Fountain format',
        'Complete character development',
        'Production-ready formatting',
        'Scene-by-scene adaptation',
        'Professional dialogue conversion'
      ]
    },
    pilot: {
      name: 'TV Pilot Episode',
      price: 29,
      icon: Tv,
      pages: '45-60',
      time: '2-5',
      features: [
        '4-act structure with cold open',
        'Episode-ready format',
        'Series setup and hooks',
        'Character introductions',
        'Season arc foundation',
        'Cliffhanger ending'
      ]
    }
  };

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-6">
              <p className="text-sm font-semibold">🎬 Professional Screenplay Conversion</p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Transform Your Novel Into a
              <br />
              <span className="text-yellow-300">Hollywood-Ready Screenplay</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-purple-100 max-w-3xl mx-auto">
              Get a professionally formatted, production-ready screenplay that meets industry standards. 
              Perfect for pitching to studios, agents, and streaming platforms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6"
                onClick={() => navigate('/register')}
              >
                Start Your Conversion Now
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                onClick={() => document.getElementById('samples').scrollIntoView({ behavior: 'smooth' })}
              >
                See Examples
              </Button>
            </div>

            {/* Quick Value Props */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12">
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <Clock className="h-8 w-8 mx-auto mb-2" />
                <div className="text-sm font-semibold">2-10 Minutes</div>
                <div className="text-xs text-purple-100">Fast Turnaround</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <FileText className="h-8 w-8 mx-auto mb-2" />
                <div className="text-sm font-semibold">Industry Format</div>
                <div className="text-xs text-purple-100">Fountain Standard</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <Award className="h-8 w-8 mx-auto mb-2" />
                <div className="text-sm font-semibold">Gold Standard</div>
                <div className="text-xs text-purple-100">Professional Quality</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <Zap className="h-8 w-8 mx-auto mb-2" />
                <div className="text-sm font-semibold">AI-Powered</div>
                <div className="text-xs text-purple-100">GPT-4o Technology</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Options */}
      <div className="py-16 bg-gray-50" id="pricing">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose Your Format</h2>
            <p className="text-lg text-gray-600">Professional screenplay conversion starting at $29</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {Object.entries(formats).map(([key, format]) => {
              const Icon = format.icon;
              return (
                <Card 
                  key={key}
                  className={`cursor-pointer transition-all hover:shadow-xl ${
                    selectedFormat === key ? 'border-purple-600 border-2 shadow-lg' : ''
                  }`}
                  onClick={() => setSelectedFormat(key)}
                >
                  <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="h-8 w-8 text-purple-600" />
                        <div>
                          <CardTitle className="text-2xl">{format.name}</CardTitle>
                          <p className="text-sm text-gray-600">{format.pages} pages • {format.time} min</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-purple-600">${format.price}</div>
                        <div className="text-xs text-gray-500">one-time</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      {format.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Button 
              size="lg" 
              className="bg-purple-600 hover:bg-purple-700 text-lg px-8 py-6"
              onClick={() => navigate('/register')}
            >
              Get Started Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Sample Output Section */}
      <div className="py-16" id="samples">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">See What You'll Get</h2>
            <p className="text-lg text-gray-600">Professional screenplay format that meets Hollywood standards</p>
          </div>

          {/* Feature Screenplay Sample */}
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="overflow-hidden shadow-xl">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      <Film className="h-6 w-6" />
                      Feature Screenplay Sample
                    </CardTitle>
                    <p className="text-sm text-purple-100 mt-1">Professional 120-page format</p>
                  </div>
                  <Star className="h-8 w-8 text-yellow-300" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="bg-gray-900 text-green-400 font-mono text-sm p-8 overflow-x-auto">
                  <pre className="whitespace-pre">{`FADE IN:

EXT. DARK FOREST - NIGHT

The MOON hangs ominously above, casting an eerie glow over the 
dense forest. The wind whispers through the trees, a haunting 
melody.

JOHN, late 30s, rugged and determined, moves cautiously through 
the shadows. His BREATH is visible in the cold night air.

                    JOHN
          Who's there?

His voice ECHOES through the trees, swallowed by the darkness. 
His heart POUNDS in his chest.

A FIGURE emerges from the shadows - it's SARAH, mid-30s, 
adventurous and enigmatic, wearing a worn leather jacket.

                    SARAH
          John! I've been looking 
          everywhere for you.

She's BREATHLESS, eyes wide with urgency.

                    JOHN
              (alarmed)
          Sarah? What are you doing 
          here? This place is dangerous!

Sarah steps closer, moonlight catching her face.

                    SARAH
          I found it, John. The artifact. 
          Everything changes now.

===

INT. ABANDONED CABIN - LATER

A single CANDLE flickers on a weathered table. John and Sarah 
sit across from each other, the golden MEDALLION between them.

                    JOHN
              (examining it)
          This can't be real. The legends 
          said it was lost forever.

                    SARAH
          I risked everything to find this. 
          Now we need to get it to safety.

She pulls out an old MAP, spreads it on the table.

                    SARAH (CONT'D)
          The museum is three days north. 
          But they're already looking for us.

John looks up sharply.

                    JOHN
          Who's looking for us?

A HELICOPTER passes overhead. They freeze.

FADE OUT.`}</pre>
                </div>
                <div className="p-6 bg-purple-50 border-t">
                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-purple-600">✓ Scene Headings</div>
                      <p className="text-sm text-gray-600">EXT./INT. format</p>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">✓ Action Blocks</div>
                      <p className="text-sm text-gray-600">Visual storytelling</p>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">✓ Dialogue</div>
                      <p className="text-sm text-gray-600">Character-driven</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* TV Pilot Sample */}
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      <Tv className="h-6 w-6" />
                      TV Pilot Sample
                    </CardTitle>
                    <p className="text-sm text-indigo-100 mt-1">Episode-ready 60-page format</p>
                  </div>
                  <Sparkles className="h-8 w-8 text-yellow-300" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="bg-gray-900 text-cyan-400 font-mono text-sm p-8 overflow-x-auto">
                  <pre className="whitespace-pre">{`**COLD OPEN**

EXT. CITY SKYLINE - DAY

The city BUSTLES below. SIRENS wail in the distance.

                    NARRATOR (V.O.)
          Every story has a beginning. 
          This one starts with a lie.

INT. COFFEE SHOP - CONTINUOUS

SARAH, 28, ambitious and sharp, sits at a corner table. Her 
laptop screen glows with a JOB APPLICATION.

A STRANGER, 40s, mysterious, approaches her table.

                    STRANGER
          Sarah Mitchell?

She looks up, confused.

                    SARAH
          Do I know you?

                    STRANGER
              (sits down)
          Not yet. But you're going 
          to want to hear what I have 
          to say.

He slides a BUSINESS CARD across the table.

                    STRANGER (CONT'D)
          Your interview today? Don't go. 
          They're not who they say they are.

Sarah stares at the card. It's BLANK.

                    SARAH
          What is this?

                    STRANGER
              (stands to leave)
          A warning. Or an opportunity. 
          Your choice.

He walks out. Sarah sits frozen, the blank card in her hand.

SMASH TO:

**ACT ONE**

INT. SARAH'S APARTMENT - NIGHT

Sarah paces, phone to her ear.

                    SARAH
              (into phone)
          Mom, I'm fine. The interview 
          went... it went.

She looks at the blank card on her desk.

                    SARAH (CONT'D)
          I'll call you tomorrow. Love you.

She hangs up. Picks up the card. Under UV light from her desk 
lamp, TEXT appears:

"MEET ME AT PIER 17. MIDNIGHT. COME ALONE."

FADE TO BLACK.`}</pre>
                </div>
                <div className="p-6 bg-indigo-50 border-t">
                  <div className="grid md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-xl font-bold text-indigo-600">✓ Cold Open</div>
                      <p className="text-sm text-gray-600">Hook viewers</p>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-indigo-600">✓ Act Breaks</div>
                      <p className="text-sm text-gray-600">TV structure</p>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-indigo-600">✓ Cliffhangers</div>
                      <p className="text-sm text-gray-600">Keep watching</p>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-indigo-600">✓ Series Setup</div>
                      <p className="text-sm text-gray-600">Season arcs</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Value Proposition */}
      <div className="py-16 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Our Conversion?</h2>
              <p className="text-lg text-gray-600">We deliver more than just formatting - we deliver opportunity</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-purple-200">
                <CardContent className="p-6">
                  <BookOpen className="h-12 w-12 text-purple-600 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Industry Standard Format</h3>
                  <p className="text-gray-600">
                    Our AI uses the same formatting standards as Hollywood professionals. 
                    Scene headings, action lines, character names, and dialogue all follow 
                    Fountain format specifications used by Final Draft and other industry tools.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200">
                <CardContent className="p-6">
                  <Award className="h-12 w-12 text-purple-600 mb-4" />
                  <h3 className="text-xl font-bold mb-2">10-Sequence Structure</h3>
                  <p className="text-gray-600">
                    Feature screenplays use the proven 10-sequence method - the same structure 
                    used in blockbuster films. Each sequence is crafted for maximum dramatic 
                    impact and pacing.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200">
                <CardContent className="p-6">
                  <TrendingUp className="h-12 w-12 text-purple-600 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Ready to Pitch</h3>
                  <p className="text-gray-600">
                    Get a screenplay that's ready to send to agents, producers, and studios. 
                    No additional formatting needed - just export as PDF and you're ready to pitch.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200">
                <CardContent className="p-6">
                  <Zap className="h-12 w-12 text-purple-600 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Fast Turnaround</h3>
                  <p className="text-gray-600">
                    Get your screenplay in minutes, not weeks. Our AI-powered conversion 
                    delivers professional results fast, so you can start pitching sooner.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to See Your Story on Screen?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Join thousands of writers who've transformed their novels into professional screenplays
          </p>
          <Button 
            size="lg" 
            className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6"
            onClick={() => navigate('/register')}
          >
            Start Converting Now <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-sm text-purple-200 mt-4">Free account • No credit card required</p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-6">
            <Logo variant="full" size="md" />
          </div>
          <div className="flex flex-wrap justify-center gap-6 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <Link to="/login" className="hover:text-white">Log In</Link>
            <Link to="/register" className="hover:text-white">Sign Up</Link>
          </div>
          <p className="text-sm">© 2024 UPost. All rights reserved.</p>
        </div>
      </div>
      </div>
    </Layout>
  );
}